import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";

type Props = {
  label: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
};

type CalendarDay = {
  date: Date;
  value: string;
  currentMonth: boolean;
};

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const weekDays = ["L", "M", "X", "J", "V", "S", "D"];

export function BibliotecaDateRangeFilter({ label, from, to, onChange }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(dateFromValue(from) ?? new Date()));

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const selectedYears = [
      visibleMonth.getFullYear(),
      dateFromValue(from)?.getFullYear(),
      dateFromValue(to)?.getFullYear(),
    ].filter((value): value is number => typeof value === "number");
    const minimum = Math.min(currentYear - 50, ...selectedYears);
    const maximum = Math.max(currentYear + 25, ...selectedYears);

    return Array.from({ length: maximum - minimum + 1 }, (_, index) => minimum + index);
  }, [from, to, visibleMonth]);

  const calendarDays = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);
  const today = toDateValue(new Date());
  const displayValue = formatRange(from, to);
  const hasAppliedRange = Boolean(from || to);
  const canApply = Boolean(draftFrom && draftTo);

  const openCalendar = () => {
    setDraftFrom(from);
    setDraftTo(to);
    setVisibleMonth(startOfMonth(dateFromValue(from || to) ?? new Date()));
    setOpen(true);
  };

  const selectDate = (value: string) => {
    if (!draftFrom || draftTo) {
      setDraftFrom(value);
      setDraftTo("");
      return;
    }

    if (value < draftFrom) {
      setDraftFrom(value);
      setDraftTo(draftFrom);
      return;
    }

    setDraftTo(value);
  };

  const applyRange = () => {
    if (!draftFrom || !draftTo) {
      return;
    }

    onChange(draftFrom, draftTo);
    setOpen(false);
  };

  const clearRange = () => {
    setDraftFrom("");
    setDraftTo("");
    onChange("", "");
    setOpen(false);
  };

  const changeMonth = (month: number) => {
    setVisibleMonth(new Date(visibleMonth.getFullYear(), month, 1));
  };

  const changeYear = (year: number) => {
    setVisibleMonth(new Date(year, visibleMonth.getMonth(), 1));
  };

  const moveMonth = (offset: number) => {
    setVisibleMonth(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1),
    );
  };

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openCalendar();
    }
  };

  return (
    <div className="biblioteca-field biblioteca-date-range-field" ref={rootRef}>
      <span>{label}</span>
      <div className="biblioteca-date-range-control">
        <button
          type="button"
          className={`biblioteca-date-range-trigger${hasAppliedRange ? " is-active" : ""}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openCalendar())}
          onKeyDown={handleTriggerKeyDown}
        >
          <CalendarDays size={16} />
          <span>{displayValue}</span>
        </button>

        {hasAppliedRange ? (
          <button
            type="button"
            className="biblioteca-date-range-clear"
            aria-label={`Limpiar ${label.toLowerCase()}`}
            title={`Limpiar ${label.toLowerCase()}`}
            onClick={clearRange}
          >
            <X size={15} />
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          className="biblioteca-date-range-popover"
          role="dialog"
          aria-label={`Seleccionar ${label.toLowerCase()}`}
        >
          <div className="biblioteca-date-range-navigation">
            <button
              type="button"
              className="biblioteca-calendar-nav-button"
              aria-label="Mes anterior"
              onClick={() => moveMonth(-1)}
            >
              <ChevronLeft size={17} />
            </button>

            <div className="biblioteca-calendar-selectors">
              <label>
                <span className="sr-only">Mes</span>
                <select
                  aria-label="Mes"
                  value={visibleMonth.getMonth()}
                  onChange={(event) => changeMonth(Number(event.target.value))}
                >
                  {monthNames.map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="sr-only">Año</span>
                <select
                  aria-label="Año"
                  value={visibleMonth.getFullYear()}
                  onChange={(event) => changeYear(Number(event.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <button
              type="button"
              className="biblioteca-calendar-nav-button"
              aria-label="Mes siguiente"
              onClick={() => moveMonth(1)}
            >
              <ChevronRight size={17} />
            </button>
          </div>

          <div className="biblioteca-calendar-weekdays" aria-hidden="true">
            {weekDays.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="biblioteca-calendar-grid">
            {calendarDays.map((day) => {
              const isStart = day.value === draftFrom;
              const isEnd = day.value === draftTo;
              const isInRange = Boolean(
                draftFrom && draftTo && day.value > draftFrom && day.value < draftTo,
              );
              const isToday = day.value === today;

              return (
                <button
                  key={day.value}
                  type="button"
                  className={[
                    "biblioteca-calendar-day",
                    day.currentMonth ? "" : "is-outside",
                    isToday ? "is-today" : "",
                    isInRange ? "is-in-range" : "",
                    isStart ? "is-range-start" : "",
                    isEnd ? "is-range-end" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={formatAccessibleDate(day.date)}
                  aria-pressed={isStart || isEnd}
                  onClick={() => selectDate(day.value)}
                >
                  {day.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="biblioteca-date-range-summary" aria-live="polite">
            <span>
              Desde <strong>{draftFrom ? formatDateValue(draftFrom) : "—"}</strong>
            </span>
            <span>
              Hasta <strong>{draftTo ? formatDateValue(draftTo) : "—"}</strong>
            </span>
          </div>

          <div className="biblioteca-date-range-actions">
            <button
              type="button"
              className="biblioteca-secondary-button"
              onClick={clearRange}
            >
              Limpiar
            </button>
            <button
              type="button"
              className="biblioteca-primary-button"
              disabled={!canApply}
              onClick={applyRange}
            >
              Aplicar rango
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildCalendarDays(month: Date): CalendarDay[] {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const firstVisibleDay = new Date(
    firstDay.getFullYear(),
    firstDay.getMonth(),
    firstDay.getDate() - mondayOffset,
  );

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(
      firstVisibleDay.getFullYear(),
      firstVisibleDay.getMonth(),
      firstVisibleDay.getDate() + index,
    );

    return {
      date,
      value: toDateValue(date),
      currentMonth: date.getMonth() === month.getMonth(),
    };
  });
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function dateFromValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function toDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateValue(value: string): string {
  const date = dateFromValue(value);

  if (!date) {
    return value;
  }

  return new Intl.DateTimeFormat("es-ES").format(date);
}

function formatRange(from: string, to: string): string {
  if (from && to) {
    return `${formatDateValue(from)} - ${formatDateValue(to)}`;
  }

  if (from) {
    return `Desde ${formatDateValue(from)}`;
  }

  if (to) {
    return `Hasta ${formatDateValue(to)}`;
  }

  return "Seleccionar rango";
}

function formatAccessibleDate(date: Date): string {
  return new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}
